import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { sessionsAPI } from '../../api/sessions';
import Card from '../../components/ui/Card';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import SessionCard from '../../components/sessions/SessionCard';
import Pagination from '../../components/ui/Pagination';

const ITEMS_PER_PAGE = 20;

const Sessions = () => {
  const { t } = useTranslation();

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('active'); // 'all', 'active', 'closed'
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, limit: ITEMS_PER_PAGE, offset: 0 });
  const [stats, setStats] = useState({ total: 0, active: 0, closed: 0 });
  const [autoRefresh, setAutoRefresh] = useState(true);
  const prevSessionsRef = useRef([]);

  useEffect(() => {
    setCurrentPage(1); // Сбрасываем на первую страницу при смене вкладки
    loadSessions(true);
    loadStats();
  }, [activeTab]);

  // Загружаем сессии при смене страницы
  useEffect(() => {
    loadSessions(false);
  }, [currentPage]);
  
  // Загружаем статистику при первом рендере
  useEffect(() => {
    loadStats();
  }, []);

  useEffect(() => {
    let interval;
    if (autoRefresh) {
      // Обновляем список каждые 30 секунд для активных сессий
      interval = setInterval(() => {
        if (activeTab === 'all' || activeTab === 'active') {
          loadSessions(false); // Не показываем загрузку при автообновлении
        }
      }, 30000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [activeTab, autoRefresh, currentPage]);

  const loadStats = async () => {
    try {
      const response = await sessionsAPI.getStats();
      setStats({
        total: response.data?.total || 0,
        active: response.data?.active || 0,
        closed: response.data?.closed || 0
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadSessions = async (isInitial = false) => {
    try {
      // Показываем загрузку только при первой загрузке
      if (isInitial || sessions.length === 0) {
        setLoading(true);
      } else {
        setIsRefreshing(true);
      }
      
      const offset = (currentPage - 1) * ITEMS_PER_PAGE;
      const params = {
        limit: ITEMS_PER_PAGE,
        offset: offset
      };
      
      if (activeTab !== 'all') {
        params.status = activeTab;
      }
      
      const response = await sessionsAPI.getSessions(params);
      const sessionsData = response.data?.sessions || [];
      
      // Инкрементальное обновление - сравниваем старый и новый списки
      if (JSON.stringify(prevSessionsRef.current) !== JSON.stringify(sessionsData)) {
        setSessions(sessionsData);
        prevSessionsRef.current = sessionsData;
      }
      
      const totalCount = response.data?.pagination?.total || sessionsData.length;
      setPagination({
        total: totalCount,
        limit: ITEMS_PER_PAGE,
        offset: offset
      });
    } catch (error) {
      console.error('Error loading sessions:', error);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageNext = () => {
    if (currentPage < getTotalPages()) {
      setCurrentPage(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePagePrev = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getTotalPages = () => {
    return Math.ceil(pagination.total / ITEMS_PER_PAGE);
  };

  // Варианты анимации для контейнера
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.03
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <div className="flex items-center space-x-2">
            <h1 className="text-3xl font-bold text-gray-900">{t('sessions.title')}</h1>
            {isRefreshing && (
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-sm text-green-600 font-medium"
              >
                • {t('sessions.page.refreshing')}
              </motion.span>
            )}
          </div>
          <p className="mt-2 text-gray-600">{t('sessions.subtitle')}</p>
        </div>
        <div className="flex items-center space-x-3">
          {/* Кнопка обновления */}
          <button
            onClick={() => loadSessions(false)}
            disabled={loading || isRefreshing}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-all duration-200 ${
              loading || isRefreshing
                ? 'border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'border-blue-500 bg-blue-50 text-blue-600 hover:bg-blue-100 hover:border-blue-600'
            }`}
            title={t('sessions.page.refreshButtonTitle')}
          >
            <svg
              className={`w-5 h-5 ${(loading || isRefreshing) ? 'animate-spin' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            <span className="font-medium">{isRefreshing ? t('sessions.page.refreshing') : t('sessions.page.refreshButton')}</span>
          </button>

          {/* Переключатель автообновления */}
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-all duration-200 ${
              autoRefresh
                ? 'border-green-500 bg-green-50 text-green-600 hover:bg-green-100 hover:border-green-600'
                : 'border-gray-300 bg-gray-50 text-gray-600 hover:bg-gray-100 hover:border-gray-400'
            }`}
            title={autoRefresh ? t('sessions.page.autoRefreshOnTitle') : t('sessions.page.autoRefreshOffTitle')}
          >
            {autoRefresh ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
            <span className="font-medium">
              {autoRefresh ? t('sessions.page.autoRefreshOn') : t('sessions.page.autoRefreshOff')}
            </span>
          </button>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('active')}
              className={`flex-1 py-4 px-6 text-center border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'active'
                  ? 'border-green-500 text-green-600 bg-green-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{t('sessions.page.tabs.active')}</span>
                <span className={`ml-2 py-0.5 px-2 rounded-full text-xs font-semibold ${
                  activeTab === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {stats.active}
                </span>
              </div>
            </button>

            <button
              onClick={() => setActiveTab('closed')}
              className={`flex-1 py-4 px-6 text-center border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'closed'
                  ? 'border-gray-500 text-gray-600 bg-gray-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{t('sessions.page.tabs.closed')}</span>
                <span className={`ml-2 py-0.5 px-2 rounded-full text-xs font-semibold ${
                  activeTab === 'closed' ? 'bg-gray-100 text-gray-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {stats.closed}
                </span>
              </div>
            </button>

            <button
              onClick={() => setActiveTab('all')}
              className={`flex-1 py-4 px-6 text-center border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'all'
                  ? 'border-blue-500 text-blue-600 bg-blue-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
                <span>{t('sessions.page.tabs.all')}</span>
                <span className={`ml-2 py-0.5 px-2 rounded-full text-xs font-semibold ${
                  activeTab === 'all' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {stats.total}
                </span>
              </div>
            </button>
          </nav>
        </div>
      </div>

      {/* Sessions List */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      ) : sessions.length === 0 ? (
        <Card className={activeTab === 'active' ? 'border-green-200 bg-green-50' : activeTab === 'closed' ? 'border-gray-200 bg-gray-50' : ''}>
          <div className="text-center py-12">
            <div className={`mx-auto h-20 w-20 rounded-full flex items-center justify-center ${
              activeTab === 'active' ? 'bg-green-100' : activeTab === 'closed' ? 'bg-gray-100' : 'bg-gray-100'
            }`}>
              {activeTab === 'active' ? (
                <svg className="h-10 w-10 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              ) : activeTab === 'closed' ? (
                <svg className="h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ) : (
                <svg className="h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              )}
            </div>
            <h3 className="mt-4 text-lg font-semibold text-gray-900">
              {activeTab === 'active' ? t('sessions.page.empty.noActive') :
               activeTab === 'closed' ? t('sessions.page.empty.noClosed') :
               t('sessions.page.empty.noAll')}
            </h3>
            <p className="mt-2 text-sm text-gray-600">
              {activeTab === 'active'
                ? t('sessions.page.empty.noActiveDesc')
                : activeTab === 'closed'
                ? t('sessions.page.empty.noClosedDesc')
                : t('sessions.page.empty.noAllDesc')}
            </p>
          </div>
        </Card>
       ) : (
        <>
          <motion.div
            className="space-y-4"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <AnimatePresence mode="popLayout">
              {sessions.map((session, index) => (
                <SessionCard
                  key={session.id}
                  session={session}
                  index={index}
                />
              ))}
            </AnimatePresence>
          </motion.div>

          {/* Пагинация */}
          {pagination.total > ITEMS_PER_PAGE && (
            <div className="mt-6 flex justify-center">
              <Pagination
                currentPage={currentPage}
                totalPages={getTotalPages()}
                onPageChange={handlePageChange}
                onPageNext={handlePageNext}
                onPagePrev={handlePagePrev}
                disabled={loading || isRefreshing}
              />
            </div>
          )}

          {/* Информация о количестве записей */}
          <div className="text-center text-sm text-gray-600">
            {t('sessions.page.showing', {
              from: Math.min((currentPage - 1) * ITEMS_PER_PAGE + 1, pagination.total),
              to: Math.min(currentPage * ITEMS_PER_PAGE, pagination.total),
              total: pagination.total
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default Sessions;
